/**
 * MeetingMinutesEntry — client-facing surface for a single posted meeting's minutes PDF.
 * Created from PostMinutesModal.jsx's "Client Hub" prompt (never manually authored in this
 * pass — see docs/CRM.md). entry.meeting = { type, pdfUrl }, resolved live server-side from the
 * Meetings Airtable record (api/_shareDocs.js#fetchMeetingRef) each time the journey loads.
 *
 * data: { phase, entryDate, revisionDue, revisionDueLabel, meetingDateStr, meetingType, postedByMemberId,
 *         postedByName } — snapshotted when the minutes post; phase/entryDate drive grouping + sort,
 *         the rest the revision request (docs/MEETING_MINUTES.md "Posting on commit")
 * (journeyOrder.js's entryPhase()/entryDateISO() already read these — no changes needed there).
 *
 * Visual language matches SpecificationEntry/PreDesignEntry in MilestoneEntry.jsx: a JourneyBook
 * opens the PDF in the portrait PdfFlipViewer, with a plain download link alongside.
 */

import { useState } from 'react'
import { J, fmtJourneyDate, PrimaryButton, GhostButton, DoneChip } from '../journeyUI.jsx'
import JourneyBook from './JourneyBook.jsx'
import PdfFlipViewer from '../../components/PdfFlipViewer.jsx'

function todayPacific() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date())
}

export default function MeetingMinutesEntry({ entry, response, onRespond, viewOnly }) {
  const [open, setOpen] = useState(false)
  // Revision requests (Matt, 2026-10-20): open until the date the posting notice named
  // (data.revisionDue, api/_revisionDue.js). The request goes to whoever posted the minutes.
  const [asking, setAsking] = useState(false)
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [sendErr, setSendErr] = useState('')
  const due = entry.data?.revisionDue || ''
  const dueLabel = entry.data?.revisionDueLabel || ''
  const windowOpen = !!due && todayPacific() <= due
  const requested = response && response.kind === 'minutes_revision' ? response : null
  async function send() {
    if (!note.trim() || !onRespond) return
    setSending(true); setSendErr('')
    try { await onRespond('minutes_revision', { note: note.trim() }); setAsking(false); setNote('') }
    catch (e) { setSendErr('That did not send. Please try again.') }
    finally { setSending(false) }
  }
  const meeting = entry.meeting || null
  const title = entry.title || (meeting?.type ? meeting.type + ' Minutes' : 'Meeting Minutes')
  const dateStr = fmtJourneyDate(entry.data?.entryDate)

  return (
    <div>
      <div style={{ textAlign: 'center', paddingTop: 10 }}>
        <div style={{ ...J.eyebrow, marginBottom: 18 }}>Meeting Minutes</div>
        <h2 style={{ ...J.title, maxWidth: 440, margin: '0 auto' }}>{title}</h2>
        {dateStr && <div style={{ ...J.mono, marginTop: 10 }}>{dateStr}</div>}
        <div style={{ width: 36, height: 1, background: 'rgba(7,39,45,0.24)', margin: '26px auto 30px' }} />
      </div>
      {meeting?.pdfUrl ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 6px' }}>
            <JourneyBook label="Minutes" aspect={8.5 / 11} onOpen={() => setOpen(true)} downloadUrl={meeting.pdfUrl} />
          </div>
          {open && <PdfFlipViewer pdfUrl={meeting.pdfUrl} title={title} onClose={() => setOpen(false)} />}
        </>
      ) : (
        <div style={{ ...J.body, fontStyle: 'italic', color: '#8ba6b1', textAlign: 'center', padding: '18px 0 8px' }}>
          The minutes document isn't available yet.
        </div>
      )}
      {meeting?.pdfUrl && (requested || windowOpen) && (
        <div style={{ maxWidth: 440, margin: '26px auto 0', textAlign: 'center' }}>
          {requested && (
            <div style={{ marginBottom: 16 }}>
              <DoneChip>Revision requested</DoneChip>
              {requested.payload?.note && <div style={{ ...J.body, marginTop: 12, fontStyle: 'italic' }}>&ldquo;{requested.payload.note}&rdquo;</div>}
            </div>
          )}
          {windowOpen && !viewOnly && (asking ? (
            <div style={{ textAlign: 'left' }}>
              <textarea value={note} onChange={e => setNote(e.target.value)} autoFocus
                placeholder="What should be corrected or added?"
                style={{
                  width: '100%', minHeight: 90, padding: 12, borderRadius: 10, resize: 'vertical',
                  border: '0.5px solid rgba(7,39,45,0.25)', fontFamily: '"DM Sans", sans-serif',
                  fontSize: 14, color: '#07272D', outline: 'none', boxSizing: 'border-box',
                }} />
              {sendErr && <div style={{ ...J.body, fontSize: 13, color: '#B3261E', marginTop: 8 }}>{sendErr}</div>}
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <PrimaryButton disabled={!note.trim() || sending} onClick={send}>{sending ? 'Sending…' : 'Send request'}</PrimaryButton>
                <GhostButton onClick={() => { setAsking(false); setNote(''); setSendErr('') }}>Cancel</GhostButton>
              </div>
            </div>
          ) : (
            <>
              {dueLabel && <div style={{ ...J.mono, marginBottom: 12 }}>Revisions can be requested until {dueLabel}</div>}
              <GhostButton onClick={() => setAsking(true)}>{requested ? 'Request another revision' : 'Request a revision'}</GhostButton>
            </>
          ))}
        </div>
      )}
    </div>
  )
}
